/**
 * @zendir/ui - useSimulationScene Hook
 *
 * Bridge between a `zendir-ts` `ZendirClient` and the `ZenSpace3D`
 * Cesium component. On mount the hook calls `getSimulationStructure`
 * to bucket simulation objects (spacecraft / ground stations) and then
 * polls each spacecraft's `Position_BN_N` on a fixed interval, emitting
 * ZenSpace3D-shaped state.
 *
 * Ground stations are resolved once per structure load (lat/lon is
 * static — no ECI polling).
 *
 * Structure JSON may use `ID`/`id` and `Type`/`type` interchangeably;
 * the walker accepts common variants. Call `refresh()` to re-fetch the
 * structure (e.g. on a consumer-owned timer) so newly spawned objects
 * appear without remounting.
 *
 * `referenceDate` is the wall-clock instant of the last successful tick
 * — pass it to `<ZenSpace3D referenceTime={...} />` so ECI/J2000
 * positions rotate to ECEF at the engine's "now".
 *
 * OPTIONAL DEPENDENCY: requires a `zendir-ts` client instance. The
 * client is passed in to avoid coupling this hook to a specific
 * session-management strategy (the consumer app owns sessions).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { GroundStation } from "../types";
import type { Satellite, Vector3D } from "../3d/ZenSpace3DTypes";

/**
 * Non-zero ECI placeholder (km) used until the first `Position_BN_N`
 * poll lands. The 3D viewer expects ECI / J2000 km positions; the
 * origin (0,0,0) sits at Earth's centre and would crash Cesium's
 * `Cartesian3.normalize` with "normalized result is not a number".
 *
 * Value: ~400 km altitude on the +X axis (vernal-equinox direction).
 * Renders just above the equator and is invisible after one tick.
 */
const PLACEHOLDER_SCENECRAFT_POSITION: Vector3D = { x: 6771, y: 0, z: 0 };

// ============================================================================
// Types
// ============================================================================

/**
 * Minimal client interface this hook depends on. Compatible with the
 * `zendir-ts` `ZendirClient` surface but typed structurally so the hook
 * can be used with any compatible client (e.g. a test fake).
 */
export interface SimulationSceneClient {
  getSimulationStructure(
    simulationId: string,
    containerId?: string,
    opts?: { signal?: AbortSignal; retry?: unknown },
  ): Promise<Record<string, unknown>>;
  getProperties(
    objectId: string,
    properties: string | string[],
    containerId?: string,
    opts?: { signal?: AbortSignal; retry?: unknown },
  ): Promise<unknown>;
  getAllProperties(
    objectId: string,
    containerId?: string,
    opts?: { signal?: AbortSignal; retry?: unknown },
  ): Promise<Record<string, unknown>>;
}

/**
 * Per-tick retry config for the position-polling loop.
 *
 * The SDK defaults `getProperties` to `engineWarmup` (24 retries /
 * ~3.6 min). That budget is right for a one-shot mount call, but
 * disastrous for a 2-second polling tick — a single warmup blip would
 * stall every subsequent tick (skip-if-busy guard) for minutes, freezing
 * the viewer. We override with a tight 2-attempt budget that rides
 * through quick blips (~1.5 s total) but cedes to the next tick fast.
 */
const TICK_RETRY: {
  maxRetries: number;
  initialDelayMs: number;
  maxDelayMs: number;
} = {
  maxRetries: 2,
  initialDelayMs: 500,
  maxDelayMs: 1500,
};

export interface UseSimulationSceneOptions {
  /** ZendirClient (or any compatible client). Pass `null` to defer. */
  client: SimulationSceneClient | null;
  /** Container ID hosting the simulation. */
  containerId: string;
  /** Simulation ID to introspect. */
  simulationId: string;
  /** Polling interval in ms for spacecraft positions. Default: 2000. */
  pollIntervalMs?: number;
  /** Pause polling without unmounting. Default: true. */
  enabled?: boolean;
}

export interface SimulationSceneObject {
  id: string;
  name: string;
  kind: "spacecraft" | "groundStation" | "celestialBody";
}

/**
 * Lightweight diagnostic snapshot of the raw `getSimulationStructure`
 * response — useful for debugging parsing mismatches in the UI without
 * shipping the full raw blob.
 */
export interface StructureDiag {
  /** Every top-level key the engine returned (e.g. "Objects","objects","Time"). */
  topLevelKeys: string[];
  /** Length of the `Objects` / `objects` array in the raw response (-1 if absent). */
  rawObjectCount: number;
  /** Length of the `Systems` / `systems` array in the raw response (-1 if absent). */
  rawSystemCount: number;
}

/**
 * Per-tick diagnostic — answers "did the position fetch succeed and what came back?".
 * Surfaced in the debug UI so a failed-but-silent tick (parser miss, engine
 * returning a different property name, all-zero positions, etc.) is visible.
 */
export interface PositionTickDiag {
  /** Wall-clock instant of the most recent tick attempt. */
  attemptedAt: Date;
  /** Number of spacecraft IDs the tick requested positions for. */
  requested: number;
  /** Number of position responses that parsed successfully. */
  parsedOk: number;
  /** Number of position responses that arrived but couldn't be parsed. */
  parseFailed: number;
  /** Number of position responses rejected by the SDK (network / 4xx / 5xx). */
  requestFailed: number;
  /**
   * The exact property names this tick asked the engine for. `null` means
   * the schema probe failed and the tick fell back to `getAllProperties`.
   * Surfaced so the debug UI can show which schema variant is in use.
   */
  requestedProps: string[] | null;
  /** All keys present on the first satellite's response (helps spot wrong property names). */
  firstResponseKeys: string[];
  /** Compact JSON of the first satellite's raw response (capped to 200 chars). */
  firstResponseSample: string;
}

export interface UseSimulationSceneResult {
  /** Spacecraft with live ECI position/velocity (km, km/s). */
  satellites: Satellite[];
  /** Static ground stations (lat/lon). */
  groundStations: GroundStation[];
  /**
   * Celestial bodies (planets, moons, sun) with live ECI positions in km.
   * The simulation's central body is filtered out — Cesium renders it
   * natively as the focal globe. Pass directly to
   * `<ZenSpace3D customObjects={scene.celestialBodies} />`.
   */
  celestialBodies: CelestialBody[];
  /** Objects discovered from `getSimulationStructure` (for diagnostics/UI lists). */
  objects: SimulationSceneObject[];
  /** True until the first structure fetch resolves. */
  isLoading: boolean;
  /**
   * Set when the structure-fetch fails (polling stops). Per-tick
   * polling errors do NOT surface here — they retry silently to keep
   * the UI from flapping during transient network blips.
   */
  error: string | null;
  /** Re-run structure fetch and a position tick. */
  refresh: () => Promise<void>;
  /**
   * UTC `Date` representing wall-clock at the most recent successful
   * tick. Pass straight into `<ZenSpace3D referenceTime={...} />` so
   * ECI/J2000 positions rotate to ECEF at "now".
   */
  referenceDate: Date;
  /**
   * Diagnostic snapshot of the raw structure response — null until the
   * first fetch resolves. Use in debug panels to verify key casing and
   * array lengths returned by the engine.
   */
  structureDiag: StructureDiag | null;
  /**
   * Diagnostic for the most recent position-polling tick — null until
   * the first tick has run. Use in debug panels to verify that
   * `getProperties` is actually returning parsable position vectors.
   */
  positionTickDiag: PositionTickDiag | null;
}

const DEFAULT_POLL_MS = 2000;
const M_TO_KM = 1 / 1000;

// ============================================================================
// Hook
// ============================================================================

export function useSimulationScene(
  options: UseSimulationSceneOptions,
): UseSimulationSceneResult {
  const {
    client,
    containerId,
    simulationId,
    pollIntervalMs = DEFAULT_POLL_MS,
    enabled = true,
  } = options;

  const [satellites, setSatellites] = useState<Satellite[]>([]);
  const [groundStations, setGroundStations] = useState<GroundStation[]>([]);
  const [celestialBodies, setCelestialBodies] = useState<CelestialBody[]>([]);
  const [objects, setObjects] = useState<SimulationSceneObject[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [referenceDate, setReferenceDate] = useState<Date>(() => new Date());
  const [structureDiag, setStructureDiag] = useState<StructureDiag | null>(
    null,
  );
  const [positionTickDiag, setPositionTickDiag] =
    useState<PositionTickDiag | null>(null);

  // Initial structure load on (client, cid, sid) change. Polling tick reads
  // positions for bucketed spacecraft; IDs live in a ref so the tick
  // callback stays stable across renders.
  const spacecraftIdsRef = useRef<string[]>([]);
  // Celestial-body IDs + names collected at structure-load time. The tick
  // polls each one's `Position` (note: NOT `Position_BN_N` — that's a
  // Spacecraft-only naming convention) plus `IsCentralBody` so we can
  // filter the focal body (Earth in Earth-centric sims) out before
  // emitting markers — Cesium renders the focal globe natively.
  const celestialBodyIdsRef = useRef<string[]>([]);
  const celestialBodyNamesRef = useRef<(string | undefined)[]>([]);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // Skip-if-busy flag: when a tick takes longer than the poll interval
  // (slow network), the next interval fires anyway. Without this guard
  // we'd stack overlapping in-flight requests indefinitely.
  const tickInFlightRef = useRef<boolean>(false);
  // Property-name schema detected from a one-time getAllProperties probe at
  // structure-load time. Different engine versions name spacecraft state
  // under different keys (`Position_BN_N` vs `Position`). The Zendir
  // `getProperties` endpoint returns 400 if ANY requested name is unknown,
  // so we cannot ask for "either" in a single call — we must know which
  // name this engine uses before the polling loop starts. `null` until
  // probed; `[]` if probe ran but no recognised key was found.
  const positionPropsRef = useRef<string[] | null>(null);

  // -------- Position polling tick (defined first so loadStructure can call it) --------

  const tickPositions = useCallback(
    async (signal: AbortSignal) => {
      if (!client) return;
      const ids = spacecraftIdsRef.current;
      if (ids.length === 0) return;
      // Skip-if-busy: never let two ticks overlap. The previous tick
      // will update state when it lands; the user sees fewer frames
      // but the engine isn't hammered with stacked requests.
      if (tickInFlightRef.current) return;
      tickInFlightRef.current = true;

      try {
        // The polling tick uses the property-name schema detected at
        // structure-load time. The Zendir `getProperties` endpoint 400s if
        // ANY requested name is unknown to the object, so we MUST know the
        // exact names before the loop starts — see `positionPropsRef`.
        //
        // Falls back to `getAllProperties` if the probe never resolved a
        // recognised position key (e.g. an exotic engine variant). That's
        // heavier per tick but bulletproof against unknown schemas.
        const propsToFetch = positionPropsRef.current;
        const useFallbackAll = !propsToFetch || propsToFetch.length === 0;

        // Fetch all spacecraft in parallel. A per-spacecraft failure is
        // settled (not thrown) so one bad object can't blank the frame —
        // we keep the previous position and retry on the next tick.
        const updates = await Promise.allSettled(
          ids.map((id) =>
            useFallbackAll
              ? // `retry: TICK_RETRY` overrides the SDK's engineWarmup default
                // for this one call. See the TICK_RETRY definition above for
                // the rationale (avoid a stuck tick stalling the viewer).
                client.getAllProperties(id, containerId, {
                  signal,
                  retry: TICK_RETRY,
                })
              : client.getProperties(id, propsToFetch!, containerId, {
                  signal,
                  retry: TICK_RETRY,
                }),
          ),
        );
        if (signal.aborted) return;

        // ---- Diagnostic snapshot ------------------------------------------
        // Captured BEFORE we update satellites so a parser bug doesn't hide
        // the raw response shape. Surfaced via `positionTickDiag` for the
        // debug UI; helps spot wrong property names / silent engine returns.
        let parsedOk = 0;
        let parseFailed = 0;
        let requestFailed = 0;
        let firstResponseKeys: string[] = [];
        let firstResponseSample = "";
        for (let i = 0; i < ids.length; i++) {
          const result = updates[i];
          if (result.status !== "fulfilled") {
            requestFailed += 1;
            continue;
          }
          if (i === 0) {
            const v = result.value as unknown;
            if (v && typeof v === "object")
              firstResponseKeys = Object.keys(v as Record<string, unknown>);
            try {
              const json = JSON.stringify(v);
              firstResponseSample =
                json.length > 200 ? `${json.slice(0, 200)}…` : json;
            } catch {
              firstResponseSample = String(v);
            }
          }
          if (parsePositionVelocity(result.value)) parsedOk += 1;
          else parseFailed += 1;
        }
        setPositionTickDiag({
          attemptedAt: new Date(),
          requested: ids.length,
          parsedOk,
          parseFailed,
          requestFailed,
          requestedProps: useFallbackAll ? null : propsToFetch,
          firstResponseKeys,
          firstResponseSample,
        });

        setSatellites((prev) => {
          if (prev.length === 0) return prev;
          const next = prev.slice();
          for (let i = 0; i < ids.length; i++) {
            const result = updates[i];
            if (result.status !== "fulfilled") continue;
            const idx = next.findIndex((sc) => sc.id === ids[i]);
            if (idx < 0) continue;
            const parsed = parsePositionVelocity(result.value);
            if (!parsed) continue;
            next[idx] = {
              ...next[idx],
              position: parsed.position,
              velocity: parsed.velocity,
            };
          }
          return next;
        });

        // ---- Celestial-body positions (independent of spacecraft tick) ----
        // Per Zendir API: CelestialBody exposes `Position` (m, ECI) and
        // `IsCentralBody` (bool). Filter the central body — it's the focal
        // globe Cesium already renders. All others become custom-object
        // markers in the viewer.
        const celIds = celestialBodyIdsRef.current;
        if (celIds.length > 0) {
          const celResults = await Promise.allSettled(
            celIds.map((id) =>
              client.getProperties(
                id,
                ["Position", "IsCentralBody"],
                containerId,
                {
                  signal,
                  retry: TICK_RETRY,
                },
              ),
            ),
          );
          if (signal.aborted) return;

          const next: CelestialBody[] = [];
          for (let i = 0; i < celIds.length; i++) {
            const result = celResults[i];
            if (result.status !== "fulfilled") continue;
            const raw = result.value as Record<string, unknown> | unknown;
            const obj =
              raw && typeof raw === "object"
                ? (raw as Record<string, unknown>)
                : null;
            if (!obj) continue;
            if (obj.IsCentralBody === true) continue; // focal globe — skip
            const posVec = parseVec3(obj.Position);
            if (!posVec) continue;
            // Engine returns metres; convert to km to match Satellite shape.
            const positionKm = scaleVec(posVec, M_TO_KM);
            const id = celIds[i];
            const name =
              celestialBodyNamesRef.current[i] ??
              `CelestialBody ${shortId(id)}`;
            next.push({ id, name, position: positionKm });
          }
          setCelestialBodies(next);
        }
      } catch {
        // Whole-tick failure (e.g. AbortError, transport blowup). The
        // outer `enabled` effect already swallows so the UI stays put.
      } finally {
        tickInFlightRef.current = false;
        if (!signal.aborted) {
          // Wall-clock reference instant — drives ECI→ECEF rotation in
          // the viewer. Bumped per tick so Cesium re-derives the
          // pseudo-fixed transform at "now".
          setReferenceDate(new Date());
        }
      }
    },
    [client, containerId],
  );

  // -------- Initial structure load + ground station resolution --------

  const loadStructure = useCallback(
    async (signal: AbortSignal) => {
      if (!client) return;
      setIsLoading(true);
      setError(null);

      try {
        const structure = await client.getSimulationStructure(
          simulationId,
          containerId,
          { signal },
        );
        if (signal.aborted) return;

        // Capture a lightweight diagnostic before any parsing so the UI can
        // show exactly what the engine returned (key names, array lengths).
        // This is the most common source of "0 objects" bugs — key-casing mismatch.
        setStructureDiag({
          topLevelKeys: Object.keys(structure),
          rawObjectCount: Array.isArray(structure.Objects)
            ? structure.Objects.length
            : Array.isArray((structure as Record<string, unknown>).objects)
              ? ((structure as Record<string, unknown>).objects as unknown[])
                  .length
              : -1,
          rawSystemCount: Array.isArray(structure.Systems)
            ? structure.Systems.length
            : Array.isArray((structure as Record<string, unknown>).systems)
              ? ((structure as Record<string, unknown>).systems as unknown[])
                  .length
              : -1,
        });

        const buckets = bucketStructure(structure);
        spacecraftIdsRef.current = buckets.spacecraft.map((s) => s.id);
        celestialBodyIdsRef.current = buckets.celestialBodyIds;
        celestialBodyNamesRef.current = buckets.celestialBodyNames;
        setSatellites(buckets.spacecraft);
        setObjects(buckets.objects);

        // ---- One-time property-name schema probe -------------------------
        // The Zendir `/get` endpoint 400s if any requested property name is
        // unknown to the object, so we cannot blindly request both legacy
        // (`Position_BN_N`) and short-form (`Position`) names in one tick.
        // Probe the first spacecraft once via `getAllProperties` (which is
        // schema-agnostic) and detect which subset of recognised names it
        // exposes. Cached in a ref for every subsequent tick.
        if (buckets.spacecraft.length > 0) {
          try {
            const probeId = buckets.spacecraft[0].id;
            const all = await client.getAllProperties(probeId, containerId, {
              signal,
            });
            if (signal.aborted) return;
            positionPropsRef.current = detectPositionPropertyNames(all);
          } catch {
            // Probe failure shouldn't block the rest of the load — leave
            // positionPropsRef as null and the tick will fall back to
            // getAllProperties per spacecraft.
            positionPropsRef.current = null;
          }
        } else {
          positionPropsRef.current = null;
        }

        // Ground stations are static — resolve their lat/lon once per structure
        // fetch. Parallel fetch with per-station settle so one bad station
        // can't block the whole render.
        const stationResults = await Promise.allSettled(
          buckets.groundStationIds.map((id) =>
            client.getAllProperties(id, containerId, { signal }),
          ),
        );
        if (signal.aborted) return;
        const stations: GroundStation[] = [];
        for (let i = 0; i < buckets.groundStationIds.length; i++) {
          const result = stationResults[i];
          const id = buckets.groundStationIds[i];
          const fallbackName = buckets.groundStationNames[i] ?? shortId(id);
          if (result.status !== "fulfilled") {
            stations.push({
              id,
              name: fallbackName,
              latitude: 0,
              longitude: 0,
            });
            continue;
          }
          stations.push(toGroundStation(id, fallbackName, result.value));
        }
        setGroundStations(stations);

        setIsLoading(false);

        // Kick off the first position tick immediately so consumers see
        // live positions without waiting a full poll interval.
        if (spacecraftIdsRef.current.length > 0) {
          await tickPositions(signal);
        }
      } catch (err) {
        if (signal.aborted) return;
        setError(
          err instanceof Error
            ? err.message
            : "Failed to load simulation structure",
        );
        setIsLoading(false);
      }
    },
    [client, containerId, simulationId, tickPositions],
  );

  // -------- Lifecycle: load on mount/id-change, poll while enabled --------

  useEffect(() => {
    if (!client || !containerId || !simulationId) {
      setIsLoading(false);
      return;
    }

    const controller = new AbortController();
    abortRef.current = controller;
    void loadStructure(controller.signal);

    return () => {
      controller.abort();
      if (abortRef.current === controller) abortRef.current = null;
    };
  }, [client, containerId, simulationId, loadStructure]);

  useEffect(() => {
    if (!enabled || !client || error) {
      // No polling when disabled or after a fatal structure-fetch error.
      // The structure-load effect above will still re-fire on id change.
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    const interval = setInterval(() => {
      const controller = new AbortController();
      // Tick errors are intentionally swallowed — see tickPositions for
      // the per-spacecraft settle policy. A network blip should not
      // crash the viewer; it should just leave the previous frame
      // visible until the next tick succeeds.
      void tickPositions(controller.signal).catch(() => undefined);
    }, pollIntervalMs);
    intervalRef.current = interval;

    return () => {
      clearInterval(interval);
      if (intervalRef.current === interval) intervalRef.current = null;
    };
  }, [client, enabled, error, pollIntervalMs, tickPositions]);

  const refresh = useCallback(async () => {
    const controller = new AbortController();
    await loadStructure(controller.signal);
  }, [loadStructure]);

  return {
    satellites,
    groundStations,
    celestialBodies,
    objects,
    isLoading,
    error,
    refresh,
    referenceDate,
    structureDiag,
    positionTickDiag,
  };
}

export default useSimulationScene;

// ============================================================================
// Structure walker
// ============================================================================

interface BucketedStructure {
  spacecraft: Satellite[];
  groundStationIds: string[];
  groundStationNames: (string | undefined)[];
  /**
   * Celestial-body IDs collected from `Type === 'CelestialBody'` nodes. Used by
   * the per-tick position poll. Names are looked up from `objects` in the
   * upstream consumer; we only need IDs here.
   */
  celestialBodyIds: string[];
  celestialBodyNames: (string | undefined)[];
  objects: SimulationSceneObject[];
}

/**
 * Celestial body with the position polled from the engine's `Position`
 * property (in km, ECI), suitable to feed straight into
 * `<ZenSpace3D customObjects={...} />`. The simulation's central body
 * (e.g. Earth in Earth-centric sims) is filtered out — Cesium renders
 * the focal globe natively, so emitting it as a marker would collide.
 */
export interface CelestialBody {
  id: string;
  name: string;
  /** ECI position in km. */
  position: { x: number; y: number; z: number };
}

/**
 * First non-empty string among PascalCase / camelCase keys for one logical field.
 */
function firstStringField(
  obj: Record<string, unknown>,
  keys: readonly string[],
): string | undefined {
  for (const key of keys) {
    const v = obj[key];
    if (typeof v === "string" && v.length > 0) return v;
  }
  return undefined;
}

/**
 * Walk a `GetSimulationStructure` response and bucket objects by Type.
 *
 * Tolerant of:
 *   - PascalCase (`Objects`, `Systems`, `Children`) and camelCase
 *     (`objects`, `systems`, `children`) — the engine has shipped both.
 *   - Objects nested at the top level, inside Systems children, or under
 *     any depth of `Children` / `children` arrays.
 */
function bucketStructure(
  structure: Record<string, unknown>,
): BucketedStructure {
  const out: BucketedStructure = {
    spacecraft: [],
    groundStationIds: [],
    groundStationNames: [],
    celestialBodyIds: [],
    celestialBodyNames: [],
    objects: [],
  };

  // Accept both PascalCase and camelCase for the top-level array keys.
  const topObjects = pickArray(structure, ["Objects", "objects"]);
  for (const obj of topObjects) walkObject(obj, out);

  // Some engine builds put spacecraft / ground stations inside a System
  // (e.g. ExtensionSystem). Walk every system entry so their children
  // are discovered regardless of nesting depth.
  const topSystems = pickArray(structure, ["Systems", "systems"]);
  for (const sys of topSystems) walkObject(sys, out);

  return out;
}

/**
 * Return the first non-empty array found under any of `keys` on `obj`.
 * Allows the caller to tolerate PascalCase / camelCase top-level array keys
 * without duplicating logic at every call site.
 */
function pickArray(obj: Record<string, unknown>, keys: string[]): unknown[] {
  for (const key of keys) {
    const v = obj[key];
    if (Array.isArray(v) && v.length > 0) return v;
  }
  // If none has items, return the first non-null array (even if empty) so
  // the caller still walks it.
  for (const key of keys) {
    const v = obj[key];
    if (Array.isArray(v)) return v;
  }
  return [];
}

function walkObject(node: unknown, out: BucketedStructure): void {
  if (!node || typeof node !== "object") return;
  const obj = node as Record<string, unknown>;
  const id = firstStringField(obj, ["ID", "id", "Id"]);
  const type = firstStringField(obj, ["Type", "type"]);
  const name = firstStringField(obj, ["Name", "name"]);

  if (id && type) {
    if (type === "Spacecraft" || type.endsWith(".Spacecraft")) {
      upsertObjectSummary(out, {
        id,
        name: name ?? `Spacecraft ${shortId(id)}`,
        kind: "spacecraft",
      });
      out.spacecraft.push({
        id,
        name: name ?? `Spacecraft ${shortId(id)}`,
        category: "satellite",
        position: { ...PLACEHOLDER_SCENECRAFT_POSITION },
        visualRadius: 50,
      });
    } else if (type === "GroundStation" || type.endsWith(".GroundStation")) {
      upsertObjectSummary(out, {
        id,
        name: name ?? `GroundStation ${shortId(id)}`,
        kind: "groundStation",
      });
      out.groundStationIds.push(id);
      out.groundStationNames.push(name);
    } else if (type === "CelestialBody" || type.endsWith(".CelestialBody")) {
      upsertObjectSummary(out, {
        id,
        name: name ?? `CelestialBody ${shortId(id)}`,
        kind: "celestialBody",
      });
      out.celestialBodyIds.push(id);
      out.celestialBodyNames.push(name);
    }
  }

  // Recurse into children regardless of parent type so a Spacecraft
  // nested under a system is still discovered. Accept both PascalCase
  // and camelCase — the engine has shipped both.
  const children = pickArray(obj, ["Children", "children"]);
  for (const child of children) walkObject(child, out);
}

function upsertObjectSummary(
  out: BucketedStructure,
  next: SimulationSceneObject,
): void {
  const idx = out.objects.findIndex((o) => o.id === next.id);
  if (idx >= 0) {
    out.objects[idx] = next;
    return;
  }
  out.objects.push(next);
}

// ============================================================================
// Schema detection
// ============================================================================

/**
 * Inspect a `getAllProperties` response and return the position property name
 * this engine exposes on a spacecraft.
 *
 * Only position is probed — velocity is intentionally excluded. The engine's
 * `/get` endpoint validates names more strictly than `getAllProperties`, and
 * `Velocity_BN_N` exists in the probe response but triggers a 400 when passed
 * to `getProperties`. Since velocity is optional (only position is used for
 * rendering), we never request it in the polling tick.
 *
 * Returns at most one name — the first position candidate whose value parses
 * as a 3-vector in the probe response.
 */
function detectPositionPropertyNames(props: Record<string, unknown>): string[] {
  const positionCandidates = ["Position_BN_N", "Position", "r_BN_N", "r_N"];

  for (const name of positionCandidates) {
    if (name in props && parseVec3(props[name]) !== null) {
      return [name];
    }
  }
  return [];
}

// ============================================================================
// Property parsing
// ============================================================================

/**
 * Engines have shipped two property-naming conventions for spacecraft state:
 *
 *   - Basilisk-style ECI: `Position_BN_N`, `Velocity_BN_N` (m, m/s)
 *   - Shorter form:       `Position`, `Velocity`           (m, m/s)
 *
 * Both are in metres. ZenSpace3D's `Vector3D` is documented as km / km/s,
 * so we scale by 1e-3. The first key that produces a parseable 3-vector wins.
 */
function parsePositionVelocity(
  raw: unknown,
): { position: Vector3D; velocity?: Vector3D } | null {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as Record<string, unknown>;
  const position =
    parseVec3(obj.Position_BN_N) ??
    parseVec3(obj.Position) ??
    parseVec3((obj as { position?: unknown }).position);
  if (!position) return null;
  // Engine returns [0,0,0] for un-initialised state (sim hasn't been ticked
  // / invoked yet). Treat that as "no data" so the upstream tick keeps the
  // previous (or placeholder) position rather than dropping the satellite
  // to Earth's centre — which crashes Cesium's Cartesian3.normalize on the
  // first render frame.
  if (position.x === 0 && position.y === 0 && position.z === 0) return null;
  const velocity =
    parseVec3(obj.Velocity_BN_N) ??
    parseVec3(obj.Velocity) ??
    parseVec3((obj as { velocity?: unknown }).velocity);
  return {
    position: scaleVec(position, M_TO_KM),
    velocity: velocity ? scaleVec(velocity, M_TO_KM) : undefined,
  };
}

/**
 * Accepts the engine's two known shapes for a 3-vector property:
 * either a `[x, y, z]` array or an object with `X/Y/Z` keys (the
 * gateway's JSON normalizer flips between the two depending on how
 * the property is declared in the model).
 */
function parseVec3(raw: unknown): Vector3D | null {
  if (Array.isArray(raw) && raw.length >= 3) {
    const [x, y, z] = raw;
    if (
      typeof x === "number" &&
      typeof y === "number" &&
      typeof z === "number"
    ) {
      return { x, y, z };
    }
  }
  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    const x = obj.X ?? obj.x;
    const y = obj.Y ?? obj.y;
    const z = obj.Z ?? obj.z;
    if (
      typeof x === "number" &&
      typeof y === "number" &&
      typeof z === "number"
    ) {
      return { x, y, z };
    }
  }
  return null;
}

function scaleVec(v: Vector3D, k: number): Vector3D {
  return { x: v.x * k, y: v.y * k, z: v.z * k };
}

// ============================================================================
// Ground station construction
// ============================================================================

function toGroundStation(
  id: string,
  fallbackName: string,
  props: Record<string, unknown>,
): GroundStation {
  const name = firstStringField(props, ["Name"]) ?? fallbackName;
  const latitude = pickNumber(props, ["Latitude", "latitude"]) ?? 0;
  const longitude = pickNumber(props, ["Longitude", "longitude"]) ?? 0;
  const elevation = pickNumber(props, ["Altitude", "altitude", "Elevation"]);
  const minElevation = pickNumber(props, ["MinElevation", "MinimumElevation"]);
  const station: GroundStation = { id, name, latitude, longitude };
  if (elevation !== undefined) station.elevation = elevation;
  if (minElevation !== undefined) station.minElevation = minElevation;
  return station;
}

function pickNumber(
  obj: Record<string, unknown>,
  keys: string[],
): number | undefined {
  for (const key of keys) {
    const v = obj[key];
    if (typeof v === "number" && Number.isFinite(v)) return v;
  }
  return undefined;
}

function shortId(id: string): string {
  return id.length > 8 ? id.slice(0, 8) : id;
}
